/**
 * useScenario hook - Access single scenario with reactivity
 * 
 * Reads scenario load status and data from MemoryStateService (single source of truth).
 * Triggers hydration via coordinator (fire-and-forget).
 * Subscribes to domain events for UI-specific transient states (access-denied, deleted).
 */

import { useState, useEffect } from 'react'
import { useServices } from '../../core/services/ServicesContext'
import { type ScenarioLoadStatus } from '../../core/state/memory-store'
import { projectScenarioForUI, type ScenarioForUI } from '../../core/queries/projections'
import { realtimeService } from '../../api/signalr'
import { eventProcessor } from '../../core/events/processor'

export interface UseScenarioResult {
  scenario: ScenarioForUI | null
  loading: boolean
  error: string | null
  accessDenied: boolean
  dismissAccessDenied: () => void
  deleted: boolean
}

export interface UseScenarioOptions {
  passive?: boolean
}

export function useScenario(scenarioId: string, options: UseScenarioOptions = {}): UseScenarioResult {
  const { memoryState, hydrationCoordinator } = useServices()
  
  // State
  const [scenario, setScenario] = useState<ScenarioForUI | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)
  const [deleted, setDeleted] = useState(false)

  // Read load status directly from memory state
  const [loadStatus, setLoadStatus] = useState<ScenarioLoadStatus>(() => 
    memoryState.getScenarioLoadStatus(scenarioId)
  )

  useEffect(() => {
    let mounted = true

    // Ensure real-time subscription is active (unless passive)
    if (!options.passive) {
      realtimeService.subscribeToScenario(scenarioId)

      // Request hydration (coordinator writes status into memoryState)
      hydrationCoordinator.ensureScenarioReady(scenarioId, 'high')
        .catch(() => {
          // Error status already written into memoryState by coordinator
          // No action needed here
        })
    } else {
      // Check if already loaded in memory
      if (memoryState.isScenarioLoaded(scenarioId)) {
        const data = memoryState.getScenarioWithData(scenarioId)
        setScenario(data ? projectScenarioForUI(data) : null)
      }
    }

    // Single subscription to memory state changes — covers BOTH status transitions AND data updates
    const unsubscribeMemory = memoryState.onStateChange(scenarioId, () => {
      if (!mounted) return

      // Read current load status
      const status = memoryState.getScenarioLoadStatus(scenarioId)
      setLoadStatus(status)

      // Update UI flags from load status
      if (status === 'access-denied') {
        setAccessDenied(true)
      } else if (status === 'deleted') {
        setDeleted(true)
      }

      // Read scenario data when loaded
      if (status === 'loaded') {
        const data = memoryState.getScenarioWithData(scenarioId)
        setScenario(data ? projectScenarioForUI(data) : null)
      }
    })

    // Subscribe to UI-specific events (access control transitions not covered by load status)
    const unsubscribeEvents = eventProcessor.onEvent(async (evt) => {
      if (!mounted) return

      if ('type' in evt && evt.type === 'access-denied' && evt.scenarioId === scenarioId) {
        setAccessDenied(true)
        return
      }

      if ('type' in evt && evt.type === 'access-restored' && evt.scenarioId === scenarioId) {
        setAccessDenied(false)
        return
      }

      if ('eventType' in evt && evt.scenarioId === scenarioId && evt.eventType === 'ScenarioDeleted') {
        setDeleted(true)
      }
    })

    return () => {
      mounted = false
      if (!options.passive) {
        realtimeService.unsubscribeFromScenario(scenarioId)
      }
      unsubscribeMemory()
      unsubscribeEvents()
    }
  }, [scenarioId, memoryState, hydrationCoordinator, options.passive])

  // Derive loading and error from load status.
  // In active (non-passive) mode, 'not-loaded' also counts as loading because
  // this hook will trigger hydration — the Outlet must not render until data arrives.
  // This prevents child components (e.g. List) from seeing null data and redirecting.
  const loading = options.passive
    ? loadStatus === 'loading'
    : (loadStatus === 'loading' || loadStatus === 'not-loaded')
  const error = loadStatus === 'error' ? 'Failed to load scenario' : null

  return {
    scenario,
    loading,
    error,
    accessDenied,
    dismissAccessDenied: () => setAccessDenied(false),
    deleted
  }
}
